// Tenantra Inc. - MFA Settings Component (Professional Style)
import { useState, useEffect } from 'react';
import { 
  Shield, Smartphone, MessageSquare, Key, CheckCircle, 
  AlertTriangle, Loader2, Copy, Eye, EyeOff, QrCode,
  RefreshCw, Lock, Trash2, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { TableSkeleton } from '@/components/dashboard/TableKit';

// MFA Method Card Component - Compact & Mobile Responsive
function MethodCard({ method, title, description, icon: Icon, isActive, onSelect, disabled }) {
  return (
    <button
      onClick={() => onSelect(method)}
      disabled={disabled || isActive}
      className={`w-full p-3 sm:p-4 text-left rounded-lg border transition-all ${
        isActive 
          ? 'border-teal-500 bg-teal-50' 
          : disabled 
            ? 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed'
            : 'border-slate-200 hover:border-teal-300 hover:bg-slate-50'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isActive ? 'bg-teal-100' : 'bg-slate-100'
        }`}>
          <Icon className={`w-5 h-5 ${isActive ? 'text-teal-600' : 'text-slate-500'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-slate-900 font-semibold text-sm sm:text-base">{title}</h3>
          <p className="text-xs sm:text-sm text-slate-500 truncate">{description}</p>
        </div>
        {isActive && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium flex-shrink-0">
            <CheckCircle className="w-3 h-3" />
            <span className="hidden sm:inline">Active</span>
          </span>
        )}
      </div>
    </button>
  );
}

// Backup Codes Display Component
function BackupCodesDisplay({ codes, onClose }) {
  const [copied, setCopied] = useState(false);

  const copyAll = () => {
    navigator.clipboard.writeText(codes.join('\n'));
    setCopied(true);
    toast.success('Backup codes copied');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-4 sm:p-5">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
            <Key className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">Backup Codes</h3>
            <p className="text-[11px] text-slate-500">Save these securely — shown only once</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-1.5 mb-4 p-3 bg-slate-50 rounded-lg">
          {codes.map((code, i) => (
            <code key={i} className="text-center py-1.5 px-2 bg-white rounded border border-slate-200 text-slate-900 font-mono text-xs">
              {code}
            </code>
          ))}
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={copyAll} className="flex-1 border-slate-200 h-8 text-xs">
            <Copy className="w-3 h-3 mr-1" />
            {copied ? 'Copied!' : 'Copy All'}
          </Button>
          <Button onClick={onClose} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white h-8 text-xs">
            I've Saved These
          </Button>
        </div>
      </div>
    </div>
  );
}

// TOTP Setup Component
function TOTPSetup({ setupData, onVerify, onCancel, loading }) {
  const [code, setCode] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [showManualKey, setShowManualKey] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onVerify(code);
  };

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-sm sm:text-base font-bold text-slate-900 mb-1">Set Up Authenticator App</h3>
        <p className="text-xs text-slate-500">
          Scan the QR code with Google Authenticator, Authy, or any TOTP app
        </p>
      </div>

      <div className="flex justify-center">
        <div className="p-3 bg-white rounded-lg border border-slate-200 shadow-sm">
          <img 
            src={`data:image/png;base64,${setupData.qr_code}`} 
            alt="TOTP QR Code"
            className="w-36 h-36 sm:w-44 sm:h-44"
          />
        </div>
      </div>

      <div className="text-center">
        <button
          onClick={() => setShowManualKey(!showManualKey)}
          className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1 mx-auto"
        >
          <QrCode className="w-3.5 h-3.5" />
          {showManualKey ? 'Hide manual key' : "Can't scan? Enter key manually"}
        </button>
        {showManualKey && (
          <div className="mt-2 p-2 bg-slate-50 rounded-lg">
            <p className="text-[10px] text-slate-500 mb-0.5">Manual Entry Key</p>
            <code className="text-xs font-mono text-slate-900 select-all break-all">
              {setupData.manual_entry_key}
            </code>
          </div>
        )}
      </div>

      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-amber-800 font-medium text-xs">Save Your Backup Codes</p>
            <p className="text-amber-700 text-[11px] mt-0.5">You'll need these if you lose access to your app.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBackupCodes(true)}
              className="mt-2 border-amber-300 text-amber-700 hover:bg-amber-100 h-7 text-[11px] px-2"
            >
              <Key className="w-3 h-3 mr-1" />
              View Backup Codes
            </Button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <Label className="text-xs text-slate-700">Enter 6-digit code from your app</Label>
          <Input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="mt-1 text-center text-xl font-mono tracking-widest h-11 bg-white border-slate-200"
            maxLength={6}
            required
            data-testid="totp-verify-code"
          />
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1 border-slate-200 h-9 text-xs">
            Cancel
          </Button>
          <Button type="submit" disabled={code.length < 6 || loading} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white h-9 text-xs">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enable MFA'}
          </Button>
        </div>
      </form>

      {showBackupCodes && (
        <BackupCodesDisplay codes={setupData.backup_codes} onClose={() => setShowBackupCodes(false)} />
      )}
    </div>
  );
}

// SMS Setup Component
function SMSSetup({ setupData, phone, onVerify, onCancel, loading }) {
  const [code, setCode] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onVerify(code);
  };

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-sm sm:text-base font-bold text-slate-900 mb-1">Set Up SMS Verification</h3>
        <p className="text-xs text-slate-500">Code sent to ***-***-{phone?.slice(-4)}</p>
      </div>

      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-amber-800 font-medium text-xs">Save Your Backup Codes</p>
            <p className="text-amber-700 text-[11px] mt-0.5">You'll need these if you can't receive SMS.</p>
            <Button variant="outline" size="sm" onClick={() => setShowBackupCodes(true)} className="mt-2 border-amber-300 text-amber-700 hover:bg-amber-100 h-7 text-[11px] px-2">
              <Key className="w-3 h-3 mr-1" /> View Backup Codes
            </Button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <Label className="text-xs text-slate-700">Enter 6-digit code from SMS</Label>
          <Input type="text" inputMode="numeric" autoComplete="off" placeholder="000000" value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="mt-1 text-center text-xl font-mono tracking-widest h-11 bg-white border-slate-200"
            maxLength={6} required data-testid="sms-verify-code" />
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1 border-slate-200 h-9 text-xs">Cancel</Button>
          <Button type="submit" disabled={code.length < 6 || loading} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white h-9 text-xs">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enable MFA'}
          </Button>
        </div>
      </form>

      {showBackupCodes && <BackupCodesDisplay codes={setupData.backup_codes} onClose={() => setShowBackupCodes(false)} />}
    </div>
  );
}

// Email Setup Component
function EmailSetup({ setupData, email, onVerify, onCancel, loading }) {
  const [code, setCode] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onVerify(code);
  };

  const maskedEmail = email ? `${email.substring(0, 3)}***@${email.split('@')[1]}` : '';

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-sm sm:text-base font-bold text-slate-900 mb-1">Set Up Email Verification</h3>
        <p className="text-xs text-slate-500">Code sent to {maskedEmail}</p>
      </div>

      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-amber-800 font-medium text-xs">Save Your Backup Codes</p>
            <p className="text-amber-700 text-[11px] mt-0.5">You'll need these if you can't receive emails.</p>
            <Button variant="outline" size="sm" onClick={() => setShowBackupCodes(true)} className="mt-2 border-amber-300 text-amber-700 hover:bg-amber-100 h-7 text-[11px] px-2">
              <Key className="w-3 h-3 mr-1" /> View Backup Codes
            </Button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <Label className="text-xs text-slate-700">Enter 6-digit code from Email</Label>
          <Input type="text" inputMode="numeric" autoComplete="off" placeholder="000000" value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="mt-1 text-center text-xl font-mono tracking-widest h-11 bg-white border-slate-200"
            maxLength={6} required data-testid="email-verify-code" />
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1 border-slate-200 h-9 text-xs">Cancel</Button>
          <Button type="submit" disabled={code.length < 6 || loading} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white h-9 text-xs">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enable MFA'}
          </Button>
        </div>
      </form>

      {showBackupCodes && <BackupCodesDisplay codes={setupData.backup_codes} onClose={() => setShowBackupCodes(false)} />}
    </div>
  );
}

// Disable MFA Component
function DisableMFA({ method, onDisable, onCancel, loading }) {
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [sentMessage, setSentMessage] = useState('');

  const needsOtpSend = method === 'sms' || method === 'email';
  const isTotp = method === 'totp';

  const sendCode = async () => {
    try {
      setSendingCode(true);
      const response = await api.post('/mfa/disable/send-code');
      setCodeSent(true);
      setSentMessage(response.data.message);
      toast.success(response.data.message);
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setSendingCode(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onDisable(code, password);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
        <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
          <Shield className="w-4 h-4 text-red-600" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-slate-900">Disable Two-Factor Authentication</h3>
          <p className="text-xs text-slate-500">This will remove the extra security layer</p>
        </div>
      </div>

      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-amber-700 text-xs flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>Disabling MFA will make your account less secure.</span>
        </p>
      </div>

      {/* For SMS/Email: Send code first */}
      {needsOtpSend && !codeSent && (
        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <p className="text-xs text-slate-600 mb-2.5">
            We'll send a verification code to your {method === 'sms' ? 'phone' : 'email'} to confirm this action.
          </p>
          <Button
            onClick={sendCode}
            disabled={sendingCode}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white h-9 text-xs"
            data-testid="send-disable-code-btn"
          >
            {sendingCode ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {sendingCode ? 'Sending...' : `Send Code via ${method === 'sms' ? 'SMS' : 'Email'}`}
          </Button>
        </div>
      )}

      {/* For TOTP: show instruction */}
      {isTotp && (
        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <p className="text-xs text-slate-600">
            Open your authenticator app and enter the current 6-digit code below. Or use one of your backup codes.
          </p>
        </div>
      )}

      {/* Code sent confirmation */}
      {needsOtpSend && codeSent && sentMessage && (
        <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
          <p className="text-xs text-teal-700 flex items-center gap-1.5">
            <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
            {sentMessage}
          </p>
        </div>
      )}

      {/* Show form only when ready */}
      {(isTotp || codeSent) && (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label className="text-xs text-slate-700">
              {isTotp ? '6-digit code from your app (or backup code)' : '6-digit code'}
            </Label>
            <Input
              type="text"
              inputMode="numeric"
              name="mfa-disable-code"
              autoComplete="off"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="mt-1 bg-white border-slate-200 text-sm h-9"
              required
              data-testid="disable-mfa-code"
            />
          </div>

          <div>
            <Label className="text-xs text-slate-700">Account Password</Label>
            <div className="relative mt-1">
              <Input
                type={showPassword ? 'text' : 'password'}
                name="mfa-disable-password"
                autoComplete="off"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white border-slate-200 pr-9 text-sm h-9"
                required
                data-testid="disable-mfa-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="flex-1 border-slate-200 h-9 text-xs"
              data-testid="cancel-disable-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!code || !password || loading}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white h-9 text-xs"
              data-testid="confirm-disable-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Disable MFA'}
            </Button>
          </div>
        </form>
      )}

      {/* Cancel button when code hasn't been sent yet */}
      {needsOtpSend && !codeSent && (
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="w-full border-slate-200 h-9 text-xs"
        >
          Cancel
        </Button>
      )}
    </div>
  );
}

// Main MFA Settings Component
export default function MFASettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupMode, setSetupMode] = useState(null); // null, 'totp', 'sms', 'email'
  const [setupData, setSetupData] = useState(null);
  const [disableMode, setDisableMode] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [userPhone, setUserPhone] = useState('');
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    fetchStatus();
    fetchUserInfo();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/mfa/status');
      setStatus(response.data);
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const response = await api.get('/auth/me');
      setUserPhone(response.data.phone || '');
      setUserEmail(response.data.email || '');
    } catch (err) {
      console.error('Failed to fetch user info');
    }
  };

  const initiateSetup = async (method) => {
    try {
      setActionLoading(true);
      const response = await api.post('/mfa/setup', { method });
      setSetupData(response.data);
      setSetupMode(method);
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const verifySetup = async (code) => {
    try {
      setActionLoading(true);
      await api.post('/mfa/setup/verify', {
        code,
        setup_token: setupData.setup_token
      });
      toast.success('MFA enabled successfully!');
      setSetupMode(null);
      setSetupData(null);
      fetchStatus();
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const disableMFA = async (code, password) => {
    try {
      setActionLoading(true);
      await api.post('/mfa/disable', { code, password });
      toast.success('MFA disabled successfully');
      setDisableMode(false);
      fetchStatus();
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const regenerateBackupCodes = async () => {
    try {
      setActionLoading(true);
      const response = await api.post('/mfa/backup-codes/regenerate');
      // Show the new codes
      setSetupData({ backup_codes: response.data.backup_codes });
      toast.success('New backup codes generated');
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <TableSkeleton rows={4} spans={[5, 4, 3]} />;
  }

  // Setup Mode
  if (setupMode === 'totp' && setupData) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <TOTPSetup
          setupData={setupData}
          onVerify={verifySetup}
          onCancel={() => { setSetupMode(null); setSetupData(null); }}
          loading={actionLoading}
        />
      </div>
    );
  }

  if (setupMode === 'sms' && setupData) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <SMSSetup
          setupData={setupData}
          phone={userPhone}
          onVerify={verifySetup}
          onCancel={() => { setSetupMode(null); setSetupData(null); }}
          loading={actionLoading}
        />
      </div>
    );
  }

  if (setupMode === 'email' && setupData) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <EmailSetup
          setupData={setupData}
          email={userEmail}
          onVerify={verifySetup}
          onCancel={() => { setSetupMode(null); setSetupData(null); }}
          loading={actionLoading}
        />
      </div>
    );
  }

  // Disable Mode
  if (disableMode) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <DisableMFA
          method={status?.method}
          onDisable={disableMFA}
          onCancel={() => setDisableMode(false)}
          loading={actionLoading}
        />
      </div>
    );
  }

  // Show backup codes after regeneration
  if (setupData?.backup_codes && !setupMode) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <BackupCodesDisplay 
          codes={setupData.backup_codes} 
          onClose={() => setSetupData(null)} 
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header - Microsoft style compact */}
      <div>
        <h1 className="text-lg font-semibold text-slate-800">Security</h1>
        <p className="text-sm text-slate-500">Manage two-factor authentication</p>
      </div>

      {/* MFA Status Row - Compact */}
      <div className="flex items-center gap-4 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${status?.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}></span>
          <span className="text-xs text-slate-600">2FA {status?.enabled ? 'Enabled' : 'Disabled'}</span>
        </div>
        {status?.enabled && (
          <>
            <div className="w-px h-4 bg-slate-200" />
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-slate-500">Method:</span>
              <span className="text-xs text-slate-700 font-medium">
                {status.method === 'totp' ? 'Authenticator' : status.method === 'email' ? 'Email' : 'SMS'}
              </span>
            </div>
            <div className="w-px h-4 bg-slate-200" />
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-slate-500">Backup:</span>
              <span className="text-xs text-slate-700 font-medium">{status.backup_codes_remaining} left</span>
            </div>
          </>
        )}
      </div>

      {/* Actions Row - Compact */}
      {status?.enabled && (
        <div className="flex items-center gap-3">
          <button 
            onClick={regenerateBackupCodes}
            disabled={actionLoading}
            className="text-xs text-teal-600 hover:text-teal-700 font-medium inline-flex items-center gap-1 hover:underline"
          >
            <RefreshCw className="w-3 h-3" />
            New backup codes
          </button>
          <button 
            onClick={() => setDisableMode(true)}
            className="text-xs text-red-600 hover:text-red-700 font-medium inline-flex items-center gap-1 hover:underline"
          >
            <Trash2 className="w-3 h-3" />
            Disable 2FA
          </button>
        </div>
      )}

      {/* MFA Methods - Compact Table style */}
      {!status?.enabled && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Choose a method</span>
          </div>
          
          <div className="divide-y divide-slate-100">
            {/* Authenticator App */}
            <button
              onClick={() => initiateSetup('totp')}
              disabled={actionLoading}
              className="w-full px-3 py-2.5 flex items-center gap-2.5 hover:bg-slate-50 transition-colors text-left"
            >
              <div className="w-7 h-7 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800">Authenticator App</div>
                <div className="text-[11px] text-slate-500">Google Authenticator, Authy, etc.</div>
              </div>
              <span className="text-[10px] text-teal-600 font-medium px-1.5 py-0.5 bg-teal-50 rounded">Recommended</span>
            </button>
            
            {/* SMS */}
            <button
              onClick={() => initiateSetup('sms')}
              disabled={actionLoading || !userPhone}
              className={`w-full px-3 py-2.5 flex items-center gap-2.5 hover:bg-slate-50 transition-colors text-left ${!userPhone ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="w-7 h-7 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800">SMS Text Message</div>
                <div className="text-[11px] text-slate-500">
                  {userPhone ? `Send to ***-***-${userPhone.slice(-4)}` : 'Add phone number first'}
                </div>
              </div>
            </button>
            
            {/* Email */}
            <button
              onClick={() => initiateSetup('email')}
              disabled={actionLoading || !userEmail}
              className={`w-full px-3 py-2.5 flex items-center gap-2.5 hover:bg-slate-50 transition-colors text-left ${!userEmail ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="w-7 h-7 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                <Mail className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800">Email Verification</div>
                <div className="text-[11px] text-slate-500">
                  {userEmail ? `Send to ${userEmail.substring(0, 3)}***@${userEmail.split('@')[1]}` : 'Email required'}
                </div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
        <h3 className="text-xs font-semibold text-slate-800 mb-1.5 flex items-center gap-1.5">
          <Lock className="w-3.5 h-3.5 text-teal-600" />
          Why use two-factor authentication?
        </h3>
        <ul className="space-y-1 text-xs text-slate-600">
          <li className="flex items-start gap-1.5">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
            Protects your account even if password is compromised
          </li>
          <li className="flex items-start gap-1.5">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
            Required for sensitive tenant and property data
          </li>
          <li className="flex items-start gap-1.5">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
            Backup codes ensure you're never locked out
          </li>
        </ul>
      </div>
    </div>
  );
}
